
import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Icon } from '@/components/ui/icon'

interface ChangePasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const newErrors: Record<string, string> = {}

    if (!currentPassword) {
      newErrors.currentPassword = 'Current password is required'
    }

    if (!newPassword) {
      newErrors.newPassword = 'New password is required'
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters'
    }

    setErrors(newErrors)

    if (Object.keys(newErrors).length === 0) {
      // TODO: Implement password change logic
      handleReset()
      onOpenChange(false)
    }
  }

  const handleReset = () => {
    setCurrentPassword('')
    setNewPassword('')
    setErrors({})
  }

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      handleReset()
    }
    onOpenChange(isOpen)
  }

  const handleForgotPassword = () => {
    // TODO: Implement forgot password flow
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <div className="flex items-center gap-1 mb-4">
            <button
              onClick={() => handleClose(false)}
              className="flex items-center gap-1 hover:bg-muted rounded px-2 py-1 transition-colors -ml-2"
            >
              <Icon name="chevron-left" className="h-4 w-4" />
              <span className="text-sm font-medium">Back</span>
            </button>
          </div>
          <DialogTitle className="text-lg font-bold">Change password</DialogTitle>
          <DialogDescription>
            Enter current password to approve that it&apos;s you
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Current Password */}
          <div className="flex flex-col gap-2 relative">
            <Label htmlFor="current-password" className="text-sm font-medium">
              Current password
            </Label>
            <Input
              id="current-password"
              type="password"
              placeholder="Password"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value)
                if (errors.currentPassword) {
                  setErrors({ ...errors, currentPassword: '' })
                }
              }}
              className={errors.currentPassword ? 'border-destructive' : ''}
            />
            <button
              type="button"
              onClick={handleForgotPassword}
              className="absolute right-0 top-0 text-sm text-muted-foreground hover:text-foreground underline"
            >
              Forgot your password?
            </button>
            {errors.currentPassword && (
              <p className="text-sm text-destructive">{errors.currentPassword}</p>
            )}
          </div>

          {/* New Password */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="new-password" className="text-sm font-medium">
              New password
            </Label>
            <Input
              id="new-password"
              type="password"
              placeholder="Password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value)
                if (errors.newPassword) {
                  setErrors({ ...errors, newPassword: '' })
                }
              }}
              className={errors.newPassword ? 'border-destructive' : ''}
            />
            {errors.newPassword && (
              <p className="text-sm text-destructive">{errors.newPassword}</p>
            )}
          </div>

          {/* Submit Button */}
          <Button type="submit" size="lg" className="w-full">
            Change password
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
